import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import { DEPTH_BAR_STYLES } from "@/lib/whale-watch/whale-watch-shared";

export type EdgeBarStyleSet = keyof typeof DEPTH_BAR_STYLES;
export type EdgeBarVariant = keyof typeof DEPTH_BAR_STYLES.desktop | "lime" | "muted";
export type EdgeBarValueFormatter = (value: number) => ReactNode;

const PLAIN_LIME_GRADIENT = "var(--color-accent-value)";

function getEdgeBarStyle(variant: EdgeBarVariant, styleSet: EdgeBarStyleSet) {
  if (variant === "lime") {
    const positiveStyle = DEPTH_BAR_STYLES[styleSet].positive;

    return {
      ...positiveStyle,
      gradient: PLAIN_LIME_GRADIENT,
      glowClass: "",
    };
  }

  if (variant === "muted") {
    return DEPTH_BAR_STYLES[styleSet].none;
  }

  return DEPTH_BAR_STYLES[styleSet][variant];
}

export function getEdgeBarWidthPercent(value: number, max: number, minPercent = 2): number {
  const rawPercent = (value / max) * 100;

  if (!Number.isFinite(rawPercent)) {
    return 0;
  }

  return Math.min(Math.max(rawPercent, minPercent), 100);
}

export interface EdgeBarRowProps {
  label: ReactNode;
  value: number;
  max: number;
  variant?: EdgeBarVariant;
  styleSet?: EdgeBarStyleSet;
  monoValue?: ReactNode;
  trailingValue?: ReactNode;
  valueFormatter?: EdgeBarValueFormatter;
  className?: string;
  monoValueClassName?: string;
  barContainerClassName?: string;
  barFillClassName?: string;
  trailingValueClassName?: string;
  barFillTestId?: string;
}

export function EdgeBarRow({
  label,
  value,
  max,
  variant = "muted",
  styleSet = "desktop",
  monoValue,
  trailingValue,
  valueFormatter,
  className = "flex items-center gap-3 py-1.5 group",
  monoValueClassName,
  barContainerClassName = "flex-1 h-5 rounded overflow-hidden relative transition-all",
  barFillClassName = "h-full rounded transition-all",
  trailingValueClassName,
  barFillTestId,
}: EdgeBarRowProps) {
  const style = getEdgeBarStyle(variant, styleSet);
  const width = getEdgeBarWidthPercent(value, max);
  const renderedTrailingValue = trailingValue ?? valueFormatter?.(value);

  return (
    <div className={className}>
      {label}
      {monoValue !== undefined && <span className={monoValueClassName}>{monoValue}</span>}
      <div className={cn(barContainerClassName, style.containerClass)}>
        <div
          className={cn(barFillClassName, style.glowClass)}
          data-testid={barFillTestId}
          style={{
            width: `${width}%`,
            background: style.gradient,
          }}
        />
      </div>
      {renderedTrailingValue !== undefined && <span className={trailingValueClassName}>{renderedTrailingValue}</span>}
    </div>
  );
}

export interface EdgeBarDividerProps {
  label: ReactNode;
  className?: string;
  lineClassName?: string;
  labelClassName?: string;
}

export function EdgeBarDivider({
  label,
  className = "flex items-center gap-3 py-2 my-1",
  lineClassName = "flex-1 border-t border-dashed border-zinc-600",
  labelClassName = "text-[10px] text-zinc-500 uppercase tracking-wider",
}: EdgeBarDividerProps) {
  return (
    <div className={className}>
      <div className={lineClassName} />
      <span className={labelClassName}>{label}</span>
      <div className={lineClassName} />
    </div>
  );
}

export interface ToolSectionHeaderProps {
  label: ReactNode;
  rightSlot?: ReactNode;
  className?: string;
  labelClassName?: string;
}

export function ToolSectionHeader({
  label,
  rightSlot,
  className = "flex items-center justify-between mb-3",
  labelClassName = "text-xs font-semibold text-zinc-400 uppercase tracking-wide",
}: ToolSectionHeaderProps) {
  return (
    <div className={className}>
      <span className={labelClassName}>{label}</span>
      {rightSlot}
    </div>
  );
}
